




import { Component, Vue, Prop, Watch } from "vue-property-decorator";
import ResizeObserver from "resize-observer-polyfill";
import {PaceDTO, YearSummaryDTO} from "@/api/pace/types";

@Component({
  name: "PaceChartHc",
})
export default class PaceChartHc extends Vue {
  @Prop({ type: Object, required: true })
  private year!: YearSummaryDTO;

  @Prop({ type: String, required: false, default: "" })
  private title!: string;
  
  private chartOptions: any = {};

  private toTimestamp(d?: Date | string | null): number | null {
    if (!d) return null;
    if (d instanceof Date) return d.getTime();
    const m = (d as string).match(/^(\d{4})-(\d{2})-(\d{2})$/);
    if (m) {
      const [_, y, mo, da] = m;
      return new Date(Number(y), Number(mo) - 1, Number(da)).getTime();
    }
    const t = new Date(d as string).getTime();
    return Number.isNaN(t) ? null : t;
  }

  private toNumber(n?: number | null): number | null {
    return typeof n === "number" && isFinite(n) ? n : null;
  }

  /** Constrói as 3 séries a partir dos paces */
  private buildSeries(paces: PaceDTO[]) {
    const obs: [number, number][] = [];
    const planting: [number, number][] = [];
    const harvest: [number, number][] = [];

    for (const p of paces || []) {
      const observableTs = this.toTimestamp(p.observableDate);
      const avgPlantingTs = this.toTimestamp(p.averagePlantingDate);
      const avgHarvestTs = this.toTimestamp(p.averageHarvestDate);

      const plantedArea = this.toNumber(p.plantedArea);
      const harvestedArea = this.toNumber(p.harvestedArea);

      if (observableTs != null && plantedArea != null) {
        obs.push([observableTs, plantedArea]);
      }
      if (avgPlantingTs != null && plantedArea != null) {
        planting.push([avgPlantingTs, plantedArea]);
      }
      if (avgHarvestTs != null && harvestedArea != null) {
        harvest.push([avgHarvestTs, harvestedArea]);
      }
    }

    // Ordena por data para linhas “limpas”
    obs.sort((a, b) => a[0] - b[0]);
    planting.sort((a, b) => a[0] - b[0]);
    harvest.sort((a, b) => a[0] - b[0]);

    return [
      {
        name: (this.$t?.("charts.pace.observablePlanted") as string) || "Data observável × área plantada",
        data: obs,
        type: "spline",
        zIndex: 3,
        marker: { radius: 3 },
        tooltip: { valueSuffix: " ha", valueDecimals: 2 },
      },
      {
        name: (this.$t?.("charts.pace.avgPlantingPlanted") as string) || "Data média de plantio × área plantada",
        data: planting,
        type: "spline",
        zIndex: 2,
        marker: { radius: 3 },
        dashStyle: "ShortDash",
        tooltip: { valueSuffix: " ha", valueDecimals: 2 },
      },
      {
        name: (this.$t?.("charts.pace.avgHarvestHarvested") as string) || "Data média de colheita × área colhida",
        data: harvest,
        type: "spline",
        zIndex: 1,
        marker: { radius: 3 },
        tooltip: { valueSuffix: " ha", valueDecimals: 2 },
      },
    ];
  }

  /** Atualiza as séries quando o DTO mudar */
  @Watch("year", { immediate: true })
  private onYearChanged(newYear: number) {
    // Evita atualizações fora do ciclo de frame
    requestAnimationFrame(() => {
      const paces = (this.year && this.year.paces) || [];
      const series = this.buildSeries(paces);
      this.$set(this.chartOptions, "series", series);
    });
  }

  private mounted() {
    // Determina o intervalo de setembro a setembro
    const baseYear = this.year?.harvestYear || new Date().getFullYear();
    const start = Date.UTC(baseYear - 1, 8, 1); // setembro do ano anterior (mês 8 = setembro)
    const end = Date.UTC(baseYear, 8, 1);       // setembro do ano atual

    this.chartOptions = {
      chart: {
        type: "line",
        backgroundColor: "#12191c",
        style: {
          fontFamily:
            "BlinkMacSystemFont,-apple-system,Segoe UI,Roboto,Oxygen,Ubuntu,Cantarell,Fira Sans,Droid Sans,Helvetica Neue,Helvetica,Arial,sans-serif",
        },
      },
      credits: { enabled: false },
      title: {
        text: this.title,
        style: { fontSize: "12px", fontWeight: "bold" },
      },
      xAxis: [
        {
          type: "datetime",
          min: start,
          max: end,
          dateTimeLabelFormats: {
            day: "%e %b %Y",
            week: "%e %b %Y",
            month: "%b %Y",
          },
        },
      ],
      yAxis: [
        {
          title: { text: this.$t?.("charts.pace.area") as string },
          min: 0,
          max: this.year.targetArea > 0 ? this.year.targetArea : null,
          plotLines: this.year?.targetArea
            ? [
              {
                color: "#ffcc00", // cor da linha
                width: 2,
                value: this.year.targetArea,
                dashStyle: "Dash",
                zIndex: 5,
                label: {
                  text: `${this.$t?.("charts.pace.targetArea")}: ${this.year.targetArea.toLocaleString()} ha`,
                  align: "right",
                  style: { color: "#ffcc00", fontSize: "10px" },
                },
              },
            ]
            : [],
        },
      ],
      tooltip: {
        shared: false,
        crosshairs: true,
        xDateFormat: "%e %b %Y",
      },
      plotOptions: {
        series: {
          connectNulls: false,
          marker: { enabled: true, radius: 3 },
        },
      },
      series: this.buildSeries((this.year && this.year.paces) || []),
    };
  }
}
